import { Link as MuiLink, Typography } from "@mui/material";
import { type FlagMetaWithDelivery } from "featureFlags/useFeatureFlagMeta";
import { DateTime } from "luxon";

/**
 * One caption line of delivery info from the flag's annotation: a link to
 * the Linear project/issue delivering the flag, and the anticipated release
 * date. Stored scalars only — the live Linear status stays drawer-only (see
 * the flags admin page's DeliverySection), so the surfaces that render this
 * (the toggle panel on the tenant Feature Flags tab, the bulk drawer) never
 * trigger a Linear API call no matter how many rows render.
 */
export function DeliveryHint({ meta }: { meta: FlagMetaWithDelivery }) {
  const { linearProjectUrl, anticipatedReleaseDate } = meta;
  if (!linearProjectUrl && !anticipatedReleaseDate) return null;
  // Parsed in UTC: this is a calendar date stored at midnight UTC, and
  // local-zone parsing would show the previous day west of Greenwich.
  const releaseDate = anticipatedReleaseDate
    ? DateTime.fromISO(anticipatedReleaseDate, { zone: "utc" }).toLocaleString(DateTime.DATE_SHORT)
    : null;
  return (
    <Typography variant="caption" color="text.secondary" display="block">
      {linearProjectUrl && (
        <MuiLink
          href={linearProjectUrl}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          // The surrounding label box expands/collapses on click; opening
          // the Linear link shouldn't also toggle the section.
          onClick={(e) => e.stopPropagation()}
        >
          Linear
        </MuiLink>
      )}
      {linearProjectUrl && releaseDate && " · "}
      {releaseDate && `Anticipated release ${releaseDate}`}
    </Typography>
  );
}
